<script setup>
import { computed, inject } from 'vue';

// Shared smartphone mockup. Used standalone by SmartphoneBlock and
// embedded by TiqHeroBlock. Body + screen + dynamic island + home
// bar + side buttons are pure CSS scaled off --tiq-phone-w so a
// single width prop redraws the whole frame proportionally.

const props = defineProps({
    mediaType: { type: String, default: 'image' }, // image | video | color | placeholder
    imageUrl: { type: String, default: '' },
    imageAlt: { type: String, default: '' },
    videoUrl: { type: String, default: '' },
    posterUrl: { type: String, default: '' },
    bgColor: { type: String, default: 'linear-gradient(160deg,#0f0c29,#302b63,#24243e)' },
    frameStyle: { type: String, default: 'dark' }, // dark | light
    maxWidth: { type: Number, default: 300 },
    hideTopbar: { type: Boolean, default: false },
    align: { type: String, default: 'center' }, // center | left | right
});

const screenStyle = computed(() => {
    if (props.mediaType === 'color') return { background: props.bgColor };
    return {};
});
const cssVars = computed(() => ({ '--tiq-phone-w-user': `${props.maxWidth}px` }));
// "No screen content" is the editor's prompt; a visitor's page leaves
// out a phone with nothing on it (the blocks decide), never labels it.
const editing = inject('tiqEditorCanvas', false);
</script>

<template>
    <div class="tiq-sp-wrap" :data-frame="frameStyle" :data-align="align"
         :data-topbar="hideTopbar ? 'hidden' : ''" :style="cssVars">
        <div class="tiq-sp">
            <!-- Side hardware buttons -->
            <span class="tiq-sp__btn tiq-sp__btn--vol-up"></span>
            <span class="tiq-sp__btn tiq-sp__btn--vol-down"></span>
            <span class="tiq-sp__btn tiq-sp__btn--power"></span>
            <div class="tiq-sp__bezel">
                <div class="tiq-sp__topbar"></div>
                <div class="tiq-sp__island"><div class="tiq-sp__camera"></div></div>
                <div class="tiq-sp__screen" :style="screenStyle">
                    <img v-if="mediaType === 'image' && imageUrl"
                         :src="imageUrl" :alt="imageAlt" class="tiq-sp__media">
                    <video v-else-if="mediaType === 'video' && videoUrl"
                           class="tiq-sp__media"
                           :src="videoUrl"
                           :poster="posterUrl || null"
                           muted autoplay loop playsinline></video>
                    <div v-else-if="editing && (mediaType === 'placeholder' || (mediaType === 'image' && !imageUrl))"
                         class="tiq-sp__placeholder">
                        <span>📱</span>
                        <span>No screen content</span>
                    </div>
                </div>
                <div class="tiq-sp__home-bar"></div>
                <div class="tiq-sp__glare"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.tiq-sp-wrap {
    --tiq-phone-w: var(--tiq-phone-w-user, 300px);
    --tiq-pad:  calc(var(--tiq-phone-w) * 16 / 300);
    --tiq-rad:  calc(var(--tiq-phone-w) * 46 / 300);
    --tiq-brad: calc(var(--tiq-phone-w) * 32 / 300);
    --phone-body:  #1c1c24;
    --phone-edge:  #2e2e3e;
    --phone-shine: #3a3a50;
    --screen-bg:   #000;
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 20px 0;
}
.tiq-sp-wrap[data-align="left"]  { justify-content: flex-start; }
.tiq-sp-wrap[data-align="right"] { justify-content: flex-end; }
.tiq-sp-wrap[data-frame="light"] {
    --phone-body:  #d6d6dc;
    --phone-edge:  #ececf0;
    --phone-shine: #ffffff;
}
.tiq-sp {
    position: relative;
    width: 100%;
    max-width: var(--tiq-phone-w);
    aspect-ratio: 300 / 620;
    background: linear-gradient(160deg,
        var(--phone-shine) 0%,
        var(--phone-edge)  30%,
        var(--phone-body)  60%,
        #141420 100%);
    border-radius: var(--tiq-rad);
    box-shadow:
        0 0 0 1px rgba(255,255,255,0.07),
        0 0 0 2px rgba(0,0,0,0.8),
        inset 0 1px 0 rgba(255,255,255,0.12),
        inset 0 -1px 0 rgba(0,0,0,0.5),
        0 40px 80px rgba(0,0,0,0.7);
    padding: var(--tiq-pad);
}
.tiq-sp-wrap[data-frame="light"] .tiq-sp {
    box-shadow:
        0 0 0 1px rgba(0,0,0,0.08),
        inset 0 1px 0 rgba(255,255,255,0.6),
        inset 0 -1px 0 rgba(0,0,0,0.1),
        0 40px 80px rgba(0,0,0,0.25);
}
.tiq-sp__btn { position: absolute; background: linear-gradient(180deg, var(--phone-shine), var(--phone-edge)); }
.tiq-sp__btn--vol-up,
.tiq-sp__btn--vol-down {
    left: calc(var(--tiq-phone-w) * -4 / 300);
    width: calc(var(--tiq-phone-w) * 4 / 300);
    height: calc(var(--tiq-phone-w) * 40 / 300);
    border-radius: 2px 0 0 2px;
}
.tiq-sp__btn--vol-up   { top: calc(var(--tiq-phone-w) * 130 / 300); }
.tiq-sp__btn--vol-down { top: calc(var(--tiq-phone-w) * 185 / 300); }
.tiq-sp__btn--power {
    right: calc(var(--tiq-phone-w) * -4 / 300);
    top:   calc(var(--tiq-phone-w) * 155 / 300);
    width: calc(var(--tiq-phone-w) * 4 / 300);
    height: calc(var(--tiq-phone-w) * 60 / 300);
    border-radius: 0 2px 2px 0;
}
.tiq-sp__bezel {
    position: relative; width: 100%; height: 100%;
    background: #0a0a0a; border-radius: var(--tiq-brad);
    overflow: hidden;
    box-shadow: inset 0 0 20px rgba(0,0,0,0.8);
}
.tiq-sp__topbar {
    position: absolute; top: 0; left: 0; right: 0;
    height: calc(var(--tiq-phone-w) * 30 / 300);
    background: #000; z-index: 15; pointer-events: none;
}
.tiq-sp-wrap[data-topbar="hidden"] .tiq-sp__topbar { display: none; }
.tiq-sp-wrap[data-topbar="hidden"] .tiq-sp__screen { top: 0; }
.tiq-sp__island {
    position: absolute;
    top: calc(var(--tiq-phone-w) * 12 / 300);
    left: 50%; transform: translateX(-50%);
    width:  calc(var(--tiq-phone-w) * 108 / 300);
    height: calc(var(--tiq-phone-w) * 32 / 300);
    background: #000;
    border-radius: calc(var(--tiq-phone-w) * 20 / 300);
    z-index: 20;
    display: flex; align-items: center; justify-content: center;
}
.tiq-sp__camera {
    width:  calc(var(--tiq-phone-w) * 10 / 300);
    height: calc(var(--tiq-phone-w) * 10 / 300);
    border-radius: 50%; background: #111;
}
.tiq-sp__screen {
    position: absolute;
    top: calc(var(--tiq-phone-w) * 30 / 300);
    right: 0; bottom: 0; left: 0;
    background: var(--screen-bg);
    overflow: hidden;
}
.tiq-sp__media {
    width: 100%; height: 100%;
    object-fit: contain; object-position: top;
    display: block; border: 0;
    background: var(--screen-bg);
}
.tiq-sp__placeholder {
    width: 100%; height: 100%;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 10px;
    background: linear-gradient(160deg,#0f0f1a 0%, #060610 100%);
    color: rgba(255,255,255,0.55);
    font-family: 'Barlow', sans-serif;
    font-size: 12px;
}
.tiq-sp__placeholder span:first-child { font-size: 36px; }
.tiq-sp__home-bar {
    position: absolute; bottom: calc(var(--tiq-phone-w) * 8 / 300);
    left: 50%; transform: translateX(-50%);
    width:  calc(var(--tiq-phone-w) * 100 / 300);
    height: calc(var(--tiq-phone-w) * 4 / 300);
    background: rgba(255,255,255,0.25);
    border-radius: 3px; z-index: 20;
}
.tiq-sp__glare {
    position: absolute;
    top: calc(var(--tiq-phone-w) * 16 / 300);
    left: calc(var(--tiq-phone-w) * 16 / 300);
    right: 60%; bottom: 50%;
    background: linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 100%);
    border-radius: var(--tiq-brad) 8px 0 0;
    pointer-events: none; z-index: 30;
}
</style>
